import {
  ActivityConfig,
  AvailableInstance,
  IntegrationsMappedDB,
  IntegrationsMappedOptions,
} from '@/types/integrations.types';
import { useMemo, useState } from 'react';
import ActivityIntegrationCard from './activity-card';
import OptionalComponent from '@ui/optional-component/optional-component';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { TooltipPortalPosition } from '@ui/tooltip-portal/tooltip-portal-types';
import Text from '@ui/text/text/text';

import './activity-mapping.css';

interface ActivityIntegrationMappingProps {
  solutionName?: string;
  setActivityMapping: (activityMapping: Record<string, ActivityConfig>) => void;
  integration: AvailableInstance;
  edit: boolean;
  isOverrideMapping?: boolean;
  eventMapping?: IntegrationsMappedDB;
  values?: IntegrationsMappedOptions;
}

const ActivityTypes: Record<string, ActivityConfig> = {
  ['submit_survey']: {
    label: 'Submitted survey',
    value: 'submit_survey',
    enabled: false,
  },
  ['submit_quiz']: {
    label: 'Submitted quiz',
    value: 'submit_quiz',
    enabled: false,
  },
  ['submit_poll']: {
    label: 'Submitted poll',
    value: 'submit_poll',
    enabled: false,
  },
  ['submit_question']: {
    label: 'Submitted question',
    value: 'submit_question',
    enabled: false,
  },
  ['submit_user_survey']: {
    label: 'Submitted feedback survey',
    value: 'submit_user_survey',
    enabled: false,
  },
  ['watch_live']: {
    label: 'Watched live',
    value: 'watch_live',
    enabled: false,
  },
  ['watch_on_demand']: {
    label: 'Watched on demand',
    value: 'watch_on_demand',
    enabled: false,
  },
};

interface LoadConfigProps {
  eventMapping: { [key: string]: ActivityConfig };
  overrideMapping?: { [key: string]: ActivityConfig };
}

const loadConfig = ({ eventMapping, overrideMapping }: LoadConfigProps) => {
  const config = { ...ActivityTypes };
  const mapping = overrideMapping ?? eventMapping;
  Object.keys(mapping).forEach((key: string) => {
    config[key] = {
      ...ActivityTypes[key],
      enabled: true,
      activityTypeId: mapping[key].activityTypeId,
    };
  });
  return config;
};

export default function ActivityIntegrationMapping({
  solutionName,
  setActivityMapping,
  integration,
  edit,
  isOverrideMapping = false,
  values,
}: ActivityIntegrationMappingProps) {
  const [config, setConfig] = useState<Record<string, ActivityConfig>>(
    edit
      ? loadConfig({
          eventMapping: integration.outbound_activity_configuration.enabled_actions,
          overrideMapping: values?.outbound_activity_configuration?.enabled_actions,
        })
      : ActivityTypes
  );
  const [defaultsExpanded, setDefaultsExpanded] = useState(false);

  const handleChangeConfig = (_config: ActivityConfig) => {
    setConfig({
      ...config,
      [_config.value]: _config,
    });
    setActivityMapping({
      ...config,
      [_config.value]: _config,
    });
  };

  const exisitingMappingDefaults = useMemo(() => {
    const enabledActions = integration?.outbound_activity_configuration?.enabled_actions;
    if (enabledActions) {
      return (
        <>
          {Object.keys(enabledActions)?.map((key: string) => {
            const activity = { ...ActivityTypes[key], activityTypeId: enabledActions[key].activityTypeId };
            return <ActivityIntegrationCard key={`default-activity-${key}`} activity={activity} disabled={true} />;
          })}
        </>
      );
    }
    return <></>;
  }, [integration?.outbound_activity_configuration?.enabled_actions]);

  return (
    <div className="activity-mapping-container">
      <OptionalComponent display={isOverrideMapping}>
        <div className="integration-defaults margin-t-24">
          <div className="grid-row">
            <label className="section-title" id="event-identification-section">
              Integration Defaults
            </label>
            <div className="flex jc-flex-end">
              <button className="clear-button no-padding" onClick={() => setDefaultsExpanded(!defaultsExpanded)}>
                <Icon
                  name={defaultsExpanded ? ICONS.KEYBOARD_ARROW_UP : ICONS.KEYBOARD_ARROW_DOWN}
                  size={16}
                  color={'white'}
                />
              </button>
            </div>
          </div>
        </div>
      </OptionalComponent>
      <OptionalComponent display={defaultsExpanded}>
        <div className="registration-defaults">
          <div className="registration-field">
            <div className="grid-row">
              <label className="registration-questions-label grid-row-label" id="registration-questions-label">
                Key
              </label>
              <label className="grid-row-label">Value</label>
            </div>
          </div>
          {exisitingMappingDefaults}
        </div>
      </OptionalComponent>
      <div className="event-identification activity-mapping-label margin-t-24">
        <div className="grid-row">
          <label className="section-title" id="activity-mapping">
            Activity Mapping
          </label>
          <TooltipPortal
            tooltipContent={
              <Text tag="p" color="var(--gray-true-dark-100)">
                Use activity tracking to update leads in {solutionName} based on their Brandlive Activity.{' '}
                <a
                  href="https://support.brandlive.com/hc/en-us/articles/31410577971099-How-to-Use-UTM-Parameters-with-Brandlive-Platform"
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more
                </a>
              </Text>
            }
            position={TooltipPortalPosition.RIGHT}
            styles={{
              width: 'fit-content',
            }}
          >
            <Icon name={ICONS.PRIMARY_TOOLTIP} size={12} color={'var(--primary-500)'} />
          </TooltipPortal>
        </div>
      </div>
      <div className="registration-field">
        {Object.keys(config).map((key, idx) => {
          return (
            <ActivityIntegrationCard
              key={`activity-${config[key].value}-${idx}`}
              activity={config[key]}
              setConfig={handleChangeConfig}
            />
          );
        })}
      </div>
    </div>
  );
}
